/**
 * Pure helpers for the Analytics tabs: period presets, number formatting,
 * change tones and the small lookups the heatmap / health cards need.
 * No React, no network - everything here is unit-tested.
 */

export type PeriodPreset =
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "last90"
  | "thisYear"
  | "custom";

export const PERIOD_PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: "last7", label: "Last 7 days" },
  { key: "last30", label: "Last 30 days" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "last90", label: "Last 90 days" },
  { key: "thisYear", label: "This year" },
  { key: "custom", label: "Custom" },
];

export const DEFAULT_PRESET: PeriodPreset = "last30";

export type DateRange = { from: string; to: string };

const DAY_MS = 24 * 3600 * 1000;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

export function isYmd(value: unknown): value is string {
  return typeof value === "string" && YMD.test(value);
}

/** Calendar day of `date` in Asia/Colombo as YYYY-MM-DD (en-CA formats that way). */
export function colomboDay(date: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Colombo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/** YYYY-MM-DD ± n days, done in UTC so DST / local zone never shifts the day. */
export function addDays(day: string, n: number): string {
  const t = Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10));
  return new Date(t + n * DAY_MS).toISOString().slice(0, 10);
}

/** Inclusive day count between two YYYY-MM-DD days (negative when reversed). */
export function daysBetween(from: string, to: string): number {
  const a = Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10));
  const b = Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10));
  return Math.round((b - a) / DAY_MS);
}

function firstOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

function firstOfPreviousMonth(day: string): string {
  const y = +day.slice(0, 4);
  const m = +day.slice(5, 7);
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, "0")}-01`;
}

/**
 * Preset → {from, to}. Data ends yesterday (the nightly job writes a day after
 * it closes), so `to` is always yesterday in Colombo and `from` never passes it.
 * `custom` keeps whatever range is passed in (defaults to the last 30 days).
 */
export function presetRange(
  preset: PeriodPreset,
  today: string = colomboDay(),
  custom?: Partial<DateRange>
): DateRange {
  const to = addDays(today, -1);
  const clamp = (from: string): DateRange => ({ from: from > to ? to : from, to });
  switch (preset) {
    case "last7":
      return clamp(addDays(to, -6));
    case "last30":
      return clamp(addDays(to, -29));
    case "thisMonth":
      return clamp(firstOfMonth(today));
    case "lastMonth": {
      const from = firstOfPreviousMonth(today);
      const lastDay = addDays(firstOfMonth(today), -1);
      return { from, to: lastDay > to ? to : lastDay };
    }
    case "last90":
      return clamp(addDays(to, -89));
    case "thisYear":
      return clamp(`${today.slice(0, 4)}-01-01`);
    case "custom":
    default: {
      const cTo = isYmd(custom?.to) && custom!.to! <= to ? custom!.to! : to;
      const cFrom = isYmd(custom?.from) ? custom!.from! : addDays(cTo, -29);
      return { from: cFrom > cTo ? cTo : cFrom, to: cTo };
    }
  }
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

export const DASH = "—";

function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function withThousands(n: number): string {
  const [int, frac] = Math.abs(n).toString().split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return (n < 0 ? "-" : "") + (frac ? `${grouped}.${frac}` : grouped);
}

/** Whole-number count with thousands separators; null → "—". */
export function formatNumber(v: number | null | undefined, digits = 0): string {
  if (!isNum(v)) return DASH;
  const rounded = digits > 0 ? Number(v.toFixed(digits)) : Math.round(v);
  return withThousands(rounded);
}

/** "Rs 1,234" - rounded to the rupee; null → "—". */
export function formatRs(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  const rounded = Math.round(v);
  return rounded < 0 ? `-Rs ${withThousands(-rounded)}` : `Rs ${withThousands(rounded)}`;
}

/** "12.3%" with one decimal; null → "—". */
export function formatPct(v: number | null | undefined, digits = 1): string {
  if (!isNum(v)) return DASH;
  return `${v.toFixed(digits)}%`;
}

/** Signed change vs the previous period: "+12.3%", "−4.0%", "0.0%"; null → "—". */
export function formatChange(change: number | null | undefined, digits = 1): string {
  if (!isNum(change)) return DASH;
  if (change > 0) return `+${change.toFixed(digits)}%`;
  if (change < 0) return `−${Math.abs(change).toFixed(digits)}%`;
  return `${(0).toFixed(digits)}%`;
}

/** 1234 → "1.2K", 2_500_000 → "2.5M"; small numbers stay whole. */
export function formatCompact(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  const trim = (n: number) => n.toFixed(1).replace(/\.0$/, "");
  if (abs >= 1_000_000) return `${sign}${trim(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}${trim(abs / 1_000)}K`;
  return `${sign}${Math.round(abs)}`;
}

/** Number with one decimal, e.g. wait minutes; null → "—". */
export function formatDecimal(v: number | null | undefined, digits = 1): string {
  if (!isNum(v)) return DASH;
  return withThousands(Number(v.toFixed(digits)));
}

/** Series bucket label: "2026-10-07" → "7 Oct", "2026-10" → "Oct 26". */
export function bucketLabel(bucket: string): string {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (/^\d{4}-\d{2}$/.test(bucket)) return `${MONTHS[+bucket.slice(5, 7) - 1]} ${bucket.slice(2, 4)}`;
  if (isYmd(bucket)) return `${+bucket.slice(8, 10)} ${MONTHS[+bucket.slice(5, 7) - 1]}`;
  return bucket;
}

/** "2026-10-07" → "7 Oct 2026" for period captions. */
export function longDate(day: string | null | undefined): string {
  if (!isYmd(day)) return DASH;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${+day.slice(8, 10)} ${MONTHS[+day.slice(5, 7) - 1]} ${day.slice(0, 4)}`;
}

/* ------------------------------------------------------------------ */
/* Tones                                                               */
/* ------------------------------------------------------------------ */

export type ChangeTone = "good" | "bad" | "neutral";

/** KPIs where going up is a problem. Everything else: up is good. */
export const LOWER_IS_BETTER = new Set([
  "noShowRate",
  "cancellationRate",
  "discount",
  "noShow",
  "cancelled",
  "waitMinutesAvg",
  "leadDaysAvg",
  "dueCreated",
]);

export function higherIsBetterFor(kpiKey: string): boolean {
  return !LOWER_IS_BETTER.has(kpiKey);
}

/** Arrow colour: an increase is good only when higher is better. */
export function changeTone(change: number | null | undefined, higherIsBetter = true): ChangeTone {
  if (!isNum(change) || change === 0) return "neutral";
  const up = change > 0;
  return up === higherIsBetter ? "good" : "bad";
}

export type HealthBand = "green" | "amber" | "red";

/** ≥80 green, 60–79 amber, <60 red; null when there is no score. */
export function healthBand(score: number | null | undefined): HealthBand | null {
  if (!isNum(score)) return null;
  if (score >= 80) return "green";
  if (score >= 60) return "amber";
  return "red";
}

/* ------------------------------------------------------------------ */
/* Heatmap / funnel / misc lookups                                     */
/* ------------------------------------------------------------------ */

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function weekdayLabel(weekday: number): string {
  return WEEKDAYS[((weekday % 7) + 7) % 7] ?? "";
}

/** 0 → "00:00", 16 → "16:00". */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/** "Wed 16:00 · 12 appointments" (singular when 1). */
export function heatmapTitle(weekday: number, hour: number, value: number): string {
  return `${weekdayLabel(weekday)} ${hourLabel(hour)} · ${formatNumber(value)} ${value === 1 ? "appointment" : "appointments"}`;
}

/** 0..1 share of the grid maximum; zero when max is 0. */
export function heatmapIntensity(value: number, max: number): number {
  if (!isNum(value) || !isNum(max) || max <= 0 || value <= 0) return 0;
  return Math.min(1, value / max);
}

export function gridMax(grid: number[][]): number {
  let max = 0;
  for (const row of grid) for (const v of row) if (isNum(v) && v > max) max = v;
  return max;
}

/** Bar width 0..100 relative to the first stage. */
export function funnelWidth(count: number | null | undefined, first: number | null | undefined): number {
  if (!isNum(count) || !isNum(first) || first <= 0 || count <= 0) return 0;
  return Math.min(100, (count / first) * 100);
}

/** Drop-off between stages as a positive %, null when the previous stage is 0. */
export function dropOffPct(previous: number | null | undefined, current: number | null | undefined): number | null {
  if (!isNum(previous) || !isNum(current) || previous <= 0) return null;
  return Math.max(0, Math.round((1 - current / previous) * 1000) / 10);
}

/** Health inputs that are not percentages. */
export function inputUnit(inputKey: string): string {
  return inputKey === "reviewScore" ? "★" : "%";
}

export const SOURCE_LABELS: Record<string, string> = {
  pre_booked: "Pre-booked",
  walk_in: "Walk-in",
  online: "Online",
  other: "Other",
};

export function sourceLabel(key: string): string {
  return SOURCE_LABELS[key] ?? key;
}

/** True when every numeric field of every point is 0 or null. */
export function seriesIsEmpty(series: Array<Record<string, unknown>> | null | undefined, keys: string[]): boolean {
  if (!series || series.length === 0) return true;
  return series.every((p) => keys.every((k) => !isNum(p[k] as number) || (p[k] as number) === 0));
}

/** Return % for a cohort column; null when the cohort is empty. */
export function cohortReturnPct(returned: number, size: number): number | null {
  if (!isNum(size) || size <= 0 || !isNum(returned)) return null;
  return Math.round((returned / size) * 1000) / 10;
}

/** Client-side page slice (1-based page). */
export function pageSlice<T>(rows: T[], page: number, pageSize: number): T[] {
  const start = (Math.max(1, page) - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

/* ------------------------------------------------------------------ */
/* Phase 2: BookMyDentist / Inventory / Reports                        */
/* ------------------------------------------------------------------ */

/** % change of `current` vs `previous`, one decimal; null when there is no previous. */
export function pctChange(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (!isNum(current) || !isNum(previous) || previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

/** 4.43 → "4.4 ★"; null → "—". */
export function formatStars(v: number | null | undefined, digits = 1): string {
  if (!isNum(v)) return DASH;
  return `${v.toFixed(digits)} ★`;
}

/** A 1–5 rating as a 0..100 bar width. */
export function ratingPct(v: number | null | undefined): number {
  if (!isNum(v) || v <= 0) return 0;
  return Math.min(100, (v / 5) * 100);
}

export const REVIEW_LABELS: Record<string, string> = {
  quality: "Quality",
  service: "Service",
  comfort: "Comfort",
  cleanliness: "Cleanliness",
  communication: "Communication",
};

export function reviewLabel(key: string): string {
  return REVIEW_LABELS[key] ?? key;
}

/** Profile checklist: complete is green, mostly done amber, otherwise red. */
export function checklistTone(percent: number | null | undefined): HealthBand | null {
  if (!isNum(percent)) return null;
  if (percent >= 100) return "green";
  if (percent >= 60) return "amber";
  return "red";
}

/** The "New" badge: a report the caller has not opened yet. */
export function reportIsNew(item: { viewed?: boolean | null }): boolean {
  return item.viewed !== true;
}

/** Newest period first ("2026-09" / "2026-09-30" sort lexically). */
export function sortReportsNewest<T extends { period: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => (a.period < b.period ? 1 : a.period > b.period ? -1 : 0));
}

/** "2026-09" → "September 2026". */
export function monthLabel(month: string | null | undefined): string {
  if (typeof month !== "string" || !/^\d{4}-\d{2}$/.test(month)) return month || DASH;
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const name = MONTHS[+month.slice(5, 7) - 1];
  return name ? `${name} ${month.slice(0, 4)}` : month;
}

/** List / detail title: the stored label for a month, the long date for a day. */
export function reportTitle(kind: "monthly" | "daily", period: string, label?: string | null): string {
  if (kind === "daily") return isYmd(period) ? longDate(period) : period;
  return label || monthLabel(period);
}

/** Tone for a stored KPI row: the row says whether higher is better. */
export function kpiRowTone(row: { change: number | null; higherIsBetter: boolean }): ChangeTone {
  return changeTone(row.change, row.higherIsBetter);
}

/** Stored KPI value by its unit ("Rs", "%" or a plain count). */
export function formatUnit(v: number | null | undefined, unit: "Rs" | "%" | ""): string {
  if (unit === "Rs") return formatRs(v);
  if (unit === "%") return formatPct(v);
  return formatNumber(v);
}

/** Score 0..100 clamped for a bar width; 0 when there is no score. */
export function scoreWidth(score: number | null | undefined): number {
  if (!isNum(score)) return 0;
  return Math.max(0, Math.min(100, score));
}

/** Quantity with its unit of measure: 12 + "box" → "12 box". */
export function formatQty(qty: number | null | undefined, scale?: string | null): string {
  const n = formatNumber(qty);
  return scale ? `${n} ${scale}` : n;
}
